import { AppShell, Avatar, Box, Burger, Container, Divider, Group, Menu, NavLink, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure, useDocumentTitle, useFocusTrap, useMediaQuery, useWindowEvent } from '@mantine/hooks';
import {
  IconBell,
  IconChevronDown,
  IconDownload,
  IconClipboardList,
  IconFolders,
  IconLayoutDashboard,
  IconLogout,
  IconUserCircle,
  type Icon,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth, useCurrentUser } from '@/auth/AuthProvider';
import { NotificationBell } from '@/layout/NotificationBell';
import { initials } from '@/lib/format';
import { useClientOptions } from './api';
import { portalOrigin, PREVIEW_BASE, usePortalTrail } from './BackButton';
import { PortalMark } from './PortalMark';
import './portal.css';

interface PortalNavItem {
  to: string;
  label: string;
  icon: Icon;
  /** Which paths count as this page. */
  match: (path: string) => boolean;
}

const WORK: PortalNavItem[] = [
  { to: '/', label: 'Dashboard', icon: IconLayoutDashboard, match: (p) => p === '/' },
  { to: '/requisitions', label: 'Dossier requisition', icon: IconClipboardList, match: (p) => p.startsWith('/requisitions') || p === '/requests/new' },
  { to: '/requests', label: 'All dossiers', icon: IconFolders, match: (p) => p === '/requests' || (p.startsWith('/requests/') && p !== '/requests/new') },
  { to: '/downloads', label: 'Ready to download', icon: IconDownload, match: (p) => p.startsWith('/downloads') },
];

const YOU: PortalNavItem[] = [
  { to: '/notifications', label: 'Notifications', icon: IconBell, match: (p) => p.startsWith('/notifications') },
  { to: '/account', label: 'Account and password', icon: IconUserCircle, match: (p) => p.startsWith('/account') },
];

function ClientMenu({ company }: { company: string | undefined }) {
  const user = useCurrentUser();
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <Menu position="bottom-end" width={240} shadow="md" withinPortal>
      <Menu.Target>
        <UnstyledButton aria-label="Account menu">
          <Group gap={8} wrap="nowrap">
            <Avatar radius="xl" size={34} color="brand">
              {initials(user.fullName)}
            </Avatar>
            <Stack gap={0} visibleFrom="sm">
              <Text fz="sm" fw={600} lh={1.2} maw={180} truncate>
                {user.fullName}
              </Text>
              <Text fz="xs" c="dimmed" lh={1.2} maw={180} truncate>
                {company ?? ' '}
              </Text>
            </Stack>
            <IconChevronDown size={14} />
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{user.email}</Menu.Label>
        <Menu.Item leftSection={<IconUserCircle size={16} />} onClick={() => navigate('/account')}>
          Account and password
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<IconLogout size={16} />}
          onClick={async () => {
            await logout();
            navigate('/login', { replace: true });
          }}
        >
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * The client portal shell: a header with the mark, notifications and account, and a side panel for the client's
 * dossiers. Nothing internal appears here. On phones the panel folds behind the menu button.
 */
export function ClientLayout() {
  useDocumentTitle('Makin RA Portal');
  const [opened, { toggle, close }] = useDisclosure(false);
  const { pathname, search } = useLocation();
  const base = pathname.startsWith(PREVIEW_BASE) ? PREVIEW_BASE : '';
  const path = pathname.slice(base.length) || '/';
  usePortalTrail(path, search);
  // A dossier keeps the page it was opened from lit, the page its back button returns to.
  const section = path.startsWith('/requests/') && path !== '/requests/new' ? (portalOrigin() ?? '/requests') : path;
  const options = useClientOptions();
  const company = options.data?.company.name;
  // On phones the open panel covers the page: keep focus inside it and let Escape close it.
  const phone = useMediaQuery('(max-width: 47.99em)') ?? false;
  const trapRef = useFocusTrap(opened && phone);

  useEffect(() => close(), [pathname, close]);
  useWindowEvent('keydown', (event) => {
    if (event.key === 'Escape' && opened) close();
  });

  const link = (item: PortalNavItem) => (
    <NavLink
      key={item.to}
      component={Link}
      to={`${base}${item.to === '/' && base ? '' : item.to}`}
      label={item.label}
      leftSection={<item.icon size={18} stroke={1.7} />}
      active={item.match(section)}
      variant="light"
      className="portal-nav-link"
    />
  );

  return (
    <AppShell
      header={{ height: 64 }}
      navbar={{ width: 248, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding={0}
      transitionDuration={150}
      transitionTimingFunction="ease-out"
      className="mexp-portal"
    >
      <AppShell.Header>
        <Group h="100%" px={{ base: 'md', sm: 'lg' }} justify="space-between" wrap="nowrap" gap="md">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label={opened ? 'Close the portal menu' : 'Open the portal menu'}
              aria-expanded={opened}
              aria-controls="portal-navbar"
            />
            <Link to={base || '/'} style={{ textDecoration: 'none', color: 'inherit' }} aria-label="Makin RA Portal dashboard">
              <Box visibleFrom="xs">
                <PortalMark />
              </Box>
              <Box hiddenFrom="xs">
                <PortalMark compact />
              </Box>
            </Link>
          </Group>
          <Group gap="sm" wrap="nowrap">
            <NotificationBell />
            <ClientMenu company={company} />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm" className="portal-navbar" id="portal-navbar" ref={trapRef}>
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={2} component="nav" aria-label="Portal">
            {WORK.map(link)}
            <Divider my="sm" />
            {YOU.map(link)}
          </Stack>
        </AppShell.Section>
        {company && (
          <AppShell.Section pt="sm">
            <Stack gap={2} px="sm" pb={4}>
              <Text fz="sm" fw={600} lh={1.3} truncate>
                {company}
              </Text>
              <Text fz="xs" c="dimmed" lh={1.4}>
                Questions about a dossier? Contact your Makin account manager.
              </Text>
            </Stack>
          </AppShell.Section>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        <Container size={1180} px={{ base: 'md', sm: 'lg', lg: 32 }} py={{ base: 'md', sm: 'lg' }} mih="calc(100dvh - 64px - 57px)">
          <Outlet />
        </Container>
        <Box component="footer" py="lg" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          <Container size={1180} px={{ base: 'md', sm: 'lg', lg: 32 }}>
            <Text fz="xs" c="dimmed">
              Makin Laboratories Pvt. Ltd. · Pithampur, Indore
            </Text>
          </Container>
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
